import { useCallback, useEffect } from "react";
import Xmpp from "@/modules/Xmpp";
import { useApp } from "@/store/app";
import { commEmitter, dataSyncEmitter } from "@/modules/events/emitter";
import { messages } from "@/modules/communication";
import clearSession from "../app/clearSession";

let isResettingStation = false;

const waitForAppStart = () =>
  new Promise<void>((resolve) => {
    const unsubscribe = useApp.subscribe((state) => {
      if (state.status !== "STARTED") return;
      unsubscribe();
      resolve();
    });
  });

export function useStationReset({
  deviceSetup,
  networkManager
}: {
  deviceSetup: CameraDeviceSetup;
  networkManager: NetworkManager;
}) {
  const setStatus = useApp((state) => state.setStatus);

  const resetStation = useCallback(async () => {
    if (isResettingStation) return;
    isResettingStation = true;
    setStatus("SIGNING_OUT");
    try {
      await Xmpp.getInstance().disconnect();
      clearSession();

      const xmppCredentials = await deviceSetup.getXmppCredentials();
      await networkManager.start(xmppCredentials);

      setStatus("STARTED");
    } catch (err) {
      log.err("Error while resetting station", err);
      if (isDev) setStatus("FAILURE");
      else restartApp({ cause: "station-reset-failed" });
    } finally {
      isResettingStation = false;
    }
  }, [deviceSetup, networkManager, setStatus]);

  const destroyDevice = useCallback(async () => {
    log.app("received unpair message or sessionToken was invalid");
    if (useApp.getState().status === "STARTING") await waitForAppStart();
    await resetStation();
  }, [resetStation]);

  useEffect(() => {
    commEmitter.on(messages.DeviceUnpairedMessage.name, destroyDevice);
    dataSyncEmitter.on("invalid-session", destroyDevice);
    dataSyncEmitter.on("device-not-paired", destroyDevice);
    return () => {
      commEmitter.off(messages.DeviceUnpairedMessage.name, destroyDevice);
      dataSyncEmitter.off("invalid-session", destroyDevice);
      dataSyncEmitter.off("device-not-paired", destroyDevice);
    };
  }, [destroyDevice]);

  return { resetStation };
}
